import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Upload, X, FileImage } from 'lucide-react';
import { useProductFeedback } from '@/hooks/useProductFeedback';
import { toast } from 'sonner';

const MODULES = [
  'Dashboard',
  'Leads',
  'Deals',
  'Contacts',
  'Cases',
  'Tasks',
  'Calls',
  'Messages (WhatsApp)',
  'Admin',
  'Settings',
  'Other',
];

export default function FeedbackForm({ open, onClose }) {
  const { t } = useTranslation('common');
  const { createFeedback, isCreating } = useProductFeedback();
  const [formData, setFormData] = useState({
    type: 'bug',
    module: '',
    title: '',
    description: '',
    priority: 'medium',
  });
  const [screenshot, setScreenshot] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error(t('productFeedback.form.errorImageType'));
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        toast.error(t('productFeedback.form.errorFileSize'));
        return;
      }

      setScreenshot(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleRemoveScreenshot = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setScreenshot(null);
    setPreviewUrl(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const isScreenshotRequired = formData.type === 'bug';

    if (!formData.type || !formData.module || !formData.title || !formData.description) {
      toast.error(t('productFeedback.form.errorRequiredFields'));
      return;
    }

    if (formData.title.length < 10 || formData.title.length > 100) {
      toast.error(t('productFeedback.form.errorTitleLength'));
      return;
    }

    if (formData.description.length < 20 || formData.description.length > 1000) {
      toast.error(t('productFeedback.form.errorDescriptionLength'));
      return;
    }

    if (isScreenshotRequired && !screenshot) {
      toast.error(t('productFeedback.form.errorScreenshotRequired'));
      return;
    }

    createFeedback(
      {
        type: formData.type,
        module: formData.module,
        title: formData.title,
        description: formData.description,
        screenshot: screenshot,
        priority: formData.priority,
      },
      {
        onSuccess: () => {
          setFormData({
            type: 'bug',
            module: '',
            title: '',
            description: '',
            priority: 'medium',
          });
          handleRemoveScreenshot();
          onClose();
        },
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('productFeedback.form.title')}</DialogTitle>
          <DialogDescription>
            {formData.type === 'bug'
              ? t('productFeedback.form.descriptionBug')
              : t('productFeedback.form.descriptionOther')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Type Selection */}
          <div>
            <Label htmlFor="type">
              {t('productFeedback.form.requestType')} <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.type}
              onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('productFeedback.form.requestTypePlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bug">
                  <div className="flex items-center gap-2">
                    <span>🐛</span>
                    <div>
                      <div className="font-medium">{t('productFeedback.form.bug')}</div>
                      <div className="text-xs text-muted-foreground">{t('productFeedback.form.bugDesc')}</div>
                    </div>
                  </div>
                </SelectItem>
                <SelectItem value="feature">
                  <div className="flex items-center gap-2">
                    <span>✨</span>
                    <div>
                      <div className="font-medium">{t('productFeedback.form.feature')}</div>
                      <div className="text-xs text-muted-foreground">{t('productFeedback.form.featureDesc')}</div>
                    </div>
                  </div>
                </SelectItem>
                <SelectItem value="improvement">
                  <div className="flex items-center gap-2">
                    <span>📈</span>
                    <div>
                      <div className="font-medium">{t('productFeedback.form.improvement')}</div>
                      <div className="text-xs text-muted-foreground">{t('productFeedback.form.improvementDesc')}</div>
                    </div>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              {t('productFeedback.form.requestTypeHint')}
            </p>
          </div>

          {/* Module Selection */}
          <div>
            <Label htmlFor="module">
              {t('productFeedback.form.module')} <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.module}
              onValueChange={(value) => setFormData(prev => ({ ...prev, module: value }))}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('productFeedback.form.moduleSelect')} />
              </SelectTrigger>
              <SelectContent>
                {MODULES.map(module => (
                  <SelectItem key={module} value={module}>
                    {module}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Title */}
          <div>
            <Label htmlFor="title">
              {t('productFeedback.form.titleLabel')} <span className="text-destructive">*</span>
              <span className="text-xs text-muted-foreground ml-2">{t('productFeedback.form.titleHint')}</span>
            </Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder={t('productFeedback.form.titlePlaceholder')}
              maxLength={100}
            />
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="description">
              {t('productFeedback.form.descriptionLabel')} <span className="text-destructive">*</span>
              <span className="text-xs text-muted-foreground ml-2">{t('productFeedback.form.descriptionHint')}</span>
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder={t('productFeedback.form.descriptionPlaceholder')}
              rows={6}
              maxLength={1000}
            />
          </div>

          {/* Priority */}
          <div>
            <Label htmlFor="priority">
              {t('productFeedback.form.priority')} <span className="text-destructive">*</span>
            </Label>
            <Select
              value={formData.priority}
              onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="low">{t('productFeedback.form.priorityLow')}</SelectItem>
                <SelectItem value="medium">{t('productFeedback.form.priorityMedium')}</SelectItem>
                <SelectItem value="high">{t('productFeedback.form.priorityHigh')}</SelectItem>
                <SelectItem value="critical">{t('productFeedback.form.priorityCritical')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Screenshot Upload */}
          <div>
            <Label htmlFor="screenshot">
              {t('productFeedback.form.screenshot')} {formData.type === 'bug' && <span className="text-destructive">*</span>}
              {formData.type !== 'bug' && <span className="text-muted-foreground text-xs ml-1">{t('productFeedback.form.screenshotOptional')}</span>}
            </Label>
            {!screenshot ? (
              <div className="mt-2">
                <label
                  htmlFor="screenshot-upload"
                  className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border rounded-lg cursor-pointer hover:bg-accent/50 transition-colors"
                >
                  <Upload className="h-8 w-8 text-muted-foreground mb-2" />
                  <span className="text-sm text-muted-foreground">
                    {t('productFeedback.form.screenshotUpload')} {formData.type !== 'bug' && t('productFeedback.form.screenshotUploadOptional')}
                  </span>
                  <span className="text-xs text-muted-foreground mt-1">
                    {t('productFeedback.form.screenshotHint')}
                  </span>
                  <input
                    id="screenshot-upload"
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                </label>
              </div>
            ) : (
              <div className="mt-2 relative">
                <div className="border border-border rounded-lg p-2">
                  {previewUrl && (
                    <img
                      src={previewUrl}
                      alt="Screenshot preview"
                      className="w-full h-48 object-contain rounded"
                    />
                  )}
                  <div className="flex items-center justify-between mt-2">
                    <div className="flex items-center gap-2 text-sm">
                      <FileImage className="h-4 w-4 text-muted-foreground" />
                      <span className="text-muted-foreground truncate max-w-xs">
                        {screenshot.name}
                      </span>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveScreenshot}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={isCreating}
            >
              {t('productFeedback.form.cancel')}
            </Button>
            <Button type="submit" disabled={isCreating}>
              {isCreating ? t('productFeedback.form.submitting') : t('productFeedback.form.submit')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
